import React from 'react';
import { Check } from 'lucide-react';
import { lireReglagesPause, heuresPayees, formaterDuree, type ReglagesPause } from '../../lib/planningPause';

/**
 * Réglages du temps de pause (maquette 26d) — un réglage de l'établissement, jamais une valeur du fichier.
 *
 * Le fichier Excel du client ne contient aucune trace de la pause : ses horaires sont des heures de
 * présence, et il déduit trente minutes par jour à la main. C'est ce qui explique les écarts entre ses
 * colonnes masquées et le calcul brut.
 *
 * **Le planning et l'import stockent toujours la présence réelle.** La déduction est un calcul
 * d'affichage appliqué à l'export, pas une transformation de la donnée : changer la pause plus tard ne
 * réécrit rien en base, cela change seulement le prochain export.
 */
const PAUSES = [
    { label: 'Aucune', valeur: 0 },
    { label: '15 min', valeur: 15 },
    { label: '30 min', valeur: 30 },
    { label: '45 min', valeur: 45 },
];

const SEUILS = [
    { label: 'Dès 4 h', valeur: 240 },
    { label: 'Dès 6 h', valeur: 360 },
    { label: 'Toute la journée', valeur: 0 },
];

/** La semaine témoin de la maquette : MINGUI REGIS sur S51, cinq jours travaillés et deux repos. */
const SEMAINE_TEMOIN = [
    { jour: 'Lundi', presence: 465 },
    { jour: 'Mardi', presence: 465 },
    { jour: 'Mercredi', presence: 465 },
    { jour: 'Jeudi', presence: 0 },
    { jour: 'Vendredi', presence: 0 },
    { jour: 'Samedi', presence: 435 },
    { jour: 'Dimanche', presence: 465 },
];

export function PauseSettings({ reglages, onChange }: {
    reglages: any;
    onChange: (partiel: Partial<ReglagesPause>) => void;
}) {
    const valeurs = lireReglagesPause(reglages);

    const portee = [
        {
            titre: 'Appliquer dans l’export Excel',
            texte: "La colonne d'heures et les totaux du fichier exporté sont nets de pause.",
            coche: valeurs.pauseDansExportExcel,
            basculer: () => onChange({ pauseDansExportExcel: !valeurs.pauseDansExportExcel }),
        },
        {
            // Libellé de la maquette, à l'endroit : coché, l'écran montre la PRÉSENCE, donc la pause n'y
            // est pas déduite. Le réglage stocké (`pauseDansPlanning`) dit l'inverse — d'où la bascule.
            titre: 'Afficher la présence dans le planning',
            texte: "À l'écran, le créneau garde ses heures d'arrivée et de départ réelles.",
            coche: !valeurs.pauseDansPlanning,
            basculer: () => onChange({ pauseDansPlanning: !valeurs.pauseDansPlanning ? true : false }),
        },
        // La maquette prévoyait ici « Déduire aussi dans la feuille de présence ». Retiré le 2026-09-19 sur
        // décision d'Emeni : la feuille de présence PDF n'a pas de colonne d'heures (Employé, Horaires,
        // Arrivée, Départ, Signature), l'interrupteur n'aurait rien eu à déduire.
    ];

    const totalPresence = SEMAINE_TEMOIN.reduce((t, j) => t + j.presence, 0);
    const totalPaye = SEMAINE_TEMOIN.reduce((t, j) => t + (j.presence ? heuresPayees(j.presence, valeurs) : 0), 0);

    return (
        <div className="flex flex-col gap-5">
            <div>
                <label className="block text-xs font-semibold mb-2 text-content">Pause déduite par jour travaillé</label>
                <div className="flex gap-[7px]">
                    {PAUSES.map((pause) => {
                        const actif = valeurs.pauseMinutes === pause.valeur;
                        return (
                            <button
                                key={pause.label}
                                type="button"
                                onClick={() => onChange({ pauseMinutes: pause.valeur })}
                                className={`flex-1 h-10 flex items-center justify-center rounded-[9px] border text-[12.5px] font-semibold whitespace-nowrap ${actif ? 'bg-content border-content text-surface' : 'bg-surface border-border text-content hover:border-content'}`}
                            >
                                {pause.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold mb-2 text-content">À partir de quelle durée de service</label>
                <div className="flex gap-[7px]">
                    {SEUILS.map((seuil) => {
                        const actif = valeurs.pauseSeuilMinutes === seuil.valeur;
                        return (
                            <button
                                key={seuil.label}
                                type="button"
                                onClick={() => onChange({ pauseSeuilMinutes: seuil.valeur })}
                                disabled={!valeurs.pauseMinutes}
                                className={`flex-1 h-10 flex items-center justify-center rounded-[9px] border text-[12.5px] font-semibold whitespace-nowrap disabled:opacity-40 ${actif ? 'bg-content border-content text-surface' : 'bg-surface border-border text-content hover:border-content'}`}
                            >
                                {seuil.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                {portee.map((item) => (
                    <label
                        key={item.titre}
                        className={`flex items-start gap-[11px] px-[13px] py-3 rounded-[10px] border cursor-pointer ${item.coche ? 'bg-surface-soft border-border' : 'bg-surface border-border'}`}
                    >
                        <input type="checkbox" className="sr-only" checked={item.coche} onChange={item.basculer} />
                        <span className={`w-[17px] h-[17px] shrink-0 mt-px rounded-[5px] border-[1.5px] flex items-center justify-center ${item.coche ? 'bg-content border-content text-surface' : 'bg-surface border-border'}`}>
                            {item.coche && <Check size={11} strokeWidth={3} />}
                        </span>
                        <span className="min-w-0">
                            <span className="block text-[12.5px] font-semibold text-content">{item.titre}</span>
                            <span className="block mt-0.5 text-[11.5px] leading-snug text-content-muted text-pretty">{item.texte}</span>
                        </span>
                    </label>
                ))}
            </div>

            <div>
                <div className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted mb-3">
                    L'effet sur une semaine de cinq jours
                </div>
                <div className="border border-border rounded-[10px] overflow-hidden">
                    <div className="grid grid-cols-[minmax(0,1fr)_76px_76px] px-3 py-2 bg-surface-soft border-b border-border">
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted">Jour</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted text-right">Présence</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.05em] text-content-muted text-right">Payé</span>
                    </div>
                    {SEMAINE_TEMOIN.map((jour) => {
                        const repos = jour.presence === 0;
                        return (
                            <div key={jour.jour} className={`grid grid-cols-[minmax(0,1fr)_76px_76px] px-3 py-2 border-b border-border/60 ${repos ? 'bg-surface-soft' : ''}`}>
                                <span className={`text-xs min-w-0 ${repos ? 'text-content-muted' : 'text-content'}`}>{jour.jour}</span>
                                <span className={`font-mono text-[11.5px] text-right tabular-nums ${repos ? 'text-content-muted' : 'text-content'}`}>
                                    {repos ? 'REPOS' : formaterDuree(jour.presence)}
                                </span>
                                <span className={`font-mono text-[11.5px] font-medium text-right tabular-nums ${repos ? 'text-content-muted' : 'text-state-ok'}`}>
                                    {repos ? '—' : formaterDuree(heuresPayees(jour.presence, valeurs))}
                                </span>
                            </div>
                        );
                    })}
                    <div className="grid grid-cols-[minmax(0,1fr)_76px_76px] px-3 py-2.5 bg-surface-soft">
                        <span className="text-xs font-bold text-content">Total</span>
                        <span className="font-mono text-xs font-medium text-right tabular-nums text-content">{formaterDuree(totalPresence)}</span>
                        <span className="font-mono text-xs font-medium text-right tabular-nums text-state-ok">{formaterDuree(totalPaye)}</span>
                    </div>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-content-muted text-pretty">
                    La pause se compte <strong className="text-content">par journée</strong>, pas par créneau — un salarié qui
                    fait midi et soir le même jour n'en perd qu'une.
                </p>
                <p className="mt-2 text-xs leading-relaxed text-content-muted text-pretty">
                    Le planning à l'écran affiche toujours la présence : c'est l'heure à laquelle le salarié arrive et part.
                    Seul l'export applique la déduction, dans sa colonne d'heures et dans les totaux.
                </p>
            </div>
        </div>
    );
}
